import React, { useEffect, useState } from "react";
import { TrendingUp, Layers, Building2, Globe2, ChevronDown } from "lucide-react";
import { api, fmtUSD } from "../../services/api";
import DecisionCard from "../../components/vans/DecisionCard";

function TypeMark({ shape, color }) {
  return <span className={`type-mark ${shape}`} style={{ "--mk": color }} />;
}

export default function DecisionsToday() {
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState(null);
  const [showBelow, setShowBelow] = useState(false);

  useEffect(() => {
    api.vansSummary().then(setSummary);
    api.vansDecisions().then(setData);
  }, []);

  if (!summary || !data) return <div className="loading-page">Correlating Vans demand signals…</div>;

  return (
    <div className="vans-page">
      <section className="vans-hero">
        <div className="vans-hero-left">
          <div className="vans-eyebrow">VF Corporation · Vans · U.S. · {summary.today}</div>
          <h1 className="vans-title">Decisions to Take Today</h1>
          <p className="vans-sub">
            DotIQ correlated independent signals — DTC sell-through, e-commerce search & conversion, DC and dealer
            inventory, returns and review sentiment, promotions — into {summary.top_count} ranked situations across
            {" "}{summary.products_monitored} styles and {summary.regions_monitored} regions. Detected 1–3 weeks before a
            single-dataset view would catch them.
          </p>
          <div className="vans-legend">
            <span className="vl-item"><TypeMark shape="triangle" color="#059669" /> Opportunity</span>
            <span className="vl-item"><TypeMark shape="circle" color="#d97706" /> Demand Not Converting</span>
            <span className="vl-item"><TypeMark shape="square" color="#dc2626" /> Product Problem</span>
          </div>
        </div>
        <div className="vans-metrics">
          <div className="vmetric hero">
            <TrendingUp size={16} />
            <div className="vm-val">{fmtUSD(summary.revenue_opportunity)}</div>
            <div className="vm-lbl">Revenue opportunity identified</div>
          </div>
          <div className="vmetric">
            <Layers size={15} />
            <div className="vm-val">{summary.top_count}</div>
            <div className="vm-lbl">Ranked situations</div>
          </div>
          <div className="vmetric">
            <Building2 size={15} />
            <div className="vm-val">{summary.systems_connected}</div>
            <div className="vm-lbl">Systems connected</div>
          </div>
          <div className="vmetric">
            <Globe2 size={15} />
            <div className="vm-val">{summary.regions_monitored}</div>
            <div className="vm-lbl">Regions monitored</div>
          </div>
        </div>
      </section>

      <div className="decision-list">
        {data.top.map((s) => <DecisionCard key={s.id} s={s} defaultOpen={s.rank === 1} />)}
      </div>

      <section className="below-section">
        <button className="below-toggle" onClick={() => setShowBelow((v) => !v)}>
          <ChevronDown size={16} className={`chev ${showBelow ? "open" : ""}`} />
          Not in today's top 5 — {data.below.length} more detected (incl. promo-driven “brand heat” filtered out)
        </button>
        {showBelow && (
          <div className="below-grid">
            {data.below.map((s) => (
              <div key={s.id} className="below-mini">
                <div className="bm-top">
                  <span className="type-pill" style={{ background: s.type_meta.soft, color: s.type_meta.text }}>
                    <TypeMark shape={s.type_meta.shape} color={s.type_meta.color} /> {s.type_label}
                  </span>
                  <span className="bm-conf">{s.confidence}%</span>
                </div>
                <div className="bm-title">{s.product} · {s.region}</div>
                {s.promo_flag ? (
                  <div className="promo-note">
                    <b>Promo-driven.</b> {s.deltas.promo_share}% of the window ran on promotion — a large share of the
                    lift is likely the discount, not organic demand. DotIQ cut its confidence and kept it out of the top 5.
                  </div>
                ) : (
                  <div className="bm-head">{s.headline}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <footer className="vans-foot">DotIQ × VF Corporation — POC2 demo · synthetic Vans data, illustrative figures</footer>
    </div>
  );
}
